import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { Outlet, NavLink, useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { PROJECT_STATUS } from "@/lib/discovery";
import { cn } from "@/lib/utils";
import { LayoutGrid, Compass, FolderOpen, ClipboardCheck, FileBarChart, ListTodo, ChevronLeft } from "lucide-react";

const ProjectCtx = createContext(null);
export const useProject = () => useContext(ProjectCtx);

const SUBNAV = [
  { to: "overview", label: "Overview", icon: LayoutGrid },
  { to: "discovery", label: "Discovery", icon: Compass },
  { to: "documents", label: "Documents", icon: FolderOpen },
  { to: "review", label: "Review", icon: ClipboardCheck },
  { to: "blueprint", label: "Blueprint", icon: FileBarChart },
  { to: "backlog", label: "Backlog", icon: ListTodo },
];

export function ProjectLayout() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);

  const refresh = useCallback(() => api.get(`/projects/${id}`).then((r) => setProject(r.data)), [id]);
  useEffect(() => { refresh(); }, [refresh]);

  if (!project) return <div className="flex flex-1 items-center justify-center text-slate-400">Loading project…</div>;
  const st = PROJECT_STATUS[project.status] || { label: project.status, tone: "slate" };

  return (
    <ProjectCtx.Provider value={{ project, refresh }}>
      <div className="sticky top-0 z-20 border-b border-slate-200/70 bg-white/80 backdrop-blur-xl">
        <div className="px-6 pt-5 lg:px-10">
          <button onClick={() => navigate("/projects")} className="mb-2 flex items-center gap-1 text-xs font-semibold text-slate-400 hover:text-slate-600">
            <ChevronLeft className="h-3.5 w-3.5" /> All Projects
          </button>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h1 className="font-heading text-2xl font-extrabold tracking-tight text-slate-900">{project.customer_name}</h1>
              <p className="mt-0.5 text-sm text-slate-500">{project.project_name}</p>
            </div>
            <div className="flex items-center gap-3">
              <span className="rounded-md bg-sky-50 px-2.5 py-1 text-xs font-semibold text-sky-700">{project.product}</span>
              <StatusBadge label={st.label} tone={st.tone} />
            </div>
          </div>
          <nav className="mt-4 flex gap-1 overflow-x-auto">
            {SUBNAV.map((n) => (
              <NavLink key={n.to} to={n.to} data-testid={`subnav-${n.to}`}
                className={({ isActive }) => cn(
                  "flex items-center gap-1.5 whitespace-nowrap border-b-2 px-3 py-2.5 text-sm font-semibold transition-colors",
                  isActive ? "border-sky-600 text-sky-700" : "border-transparent text-slate-500 hover:text-slate-800"
                )}>
                <n.icon className="h-4 w-4" /> {n.label}
              </NavLink>
            ))}
          </nav>
        </div>
      </div>
      <div className="flex-1"><Outlet /></div>
    </ProjectCtx.Provider>
  );
}
